/**
 * VideoPage — Video-Erstellungs-Flow
 *
 * Tabs:
 *   1. Video erstellen  — POST /api/video/generate (Einzelgenerierung)
 *   2. Batch            — POST /api/video/generate-batch
 *   3. Logs             — GET  /api/video/logs
 *
 * Provider-Status-Panel zeigt LONGFORM (Longcat) + SHORTFORM (WAN / Seedance-Stub)
 * separat an, basierend auf GET /api/video/providers.
 *
 * Erreichbar unter /video.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Video, Loader2, CheckCircle, AlertCircle, Info,
  Sparkles, Play, ExternalLink, RefreshCw, ChevronDown,
  List, Clock, Zap, FileVideo, ArrowRight,
  Search, ChevronLeft, ChevronRight, Calendar,
} from "lucide-react";
import { VIDEO_BATCH_ENABLED } from "../config/features";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Kanal-Definition (kanonische Quelle im Frontend) ─────────────────────────
// Muss mit PLATFORM_PRESETS in video_providers.py übereinstimmen.
//
// Feld "release": "live" | "prepared"
//   "live"     — Kanal ist technisch voll angebunden, Publishing geht produktiv.
//   "prepared" — Kanal ist architektonisch vorbereitet, Publishing noch nicht freigeschaltet
//                (z.B. wartet auf API-Review / Meta App Permissions).
export const CHANNELS = [
  // SHORTFORM — kurze Clips
  { value: "tiktok",          label: "TikTok (9:16)",            type: "SHORTFORM", icon: "📱", release: "live" },
  { value: "instagram",       label: "Instagram Reel (9:16)",    type: "SHORTFORM", icon: "📸", release: "live" },
  { value: "instagram_reel",  label: "Instagram Reel alt (9:16)",type: "SHORTFORM", icon: "📸", release: "live" },
  { value: "youtube_short",   label: "YouTube Short (9:16)",     type: "SHORTFORM", icon: "▶️", release: "live" },
  // Threads — text-first, Video optional im 9:16-Format (Meta-Specs wie Reels).
  // Publishing noch nicht freigeschaltet — wartet auf Meta App Review.
  { value: "threads",         label: "Threads (Video optional)", type: "SHORTFORM", icon: "@",  release: "prepared" },
  // LONGFORM — längere Videos
  { value: "linkedin",        label: "LinkedIn Video (16:9)",    type: "LONGFORM",  icon: "💼", release: "live" },
  { value: "facebook",        label: "Facebook Video (16:9)",    type: "LONGFORM",  icon: "👥", release: "live" },
  { value: "youtube",         label: "YouTube (16:9)",           type: "LONGFORM",  icon: "▶️", release: "live" },
  { value: "blog",            label: "Blog-Teaser (16:9)",       type: "LONGFORM",  icon: "📝", release: "live" },
  { value: "landscape_ad",    label: "Landscape-Ad (16:9)",      type: "LONGFORM",  icon: "🖥️", release: "live" },
];

// Kanäle für den normalen Einzelgenerator (instagram_reel und landscape_ad ausgeblendet, Duplikat)
export const CHANNELS_MAIN = CHANNELS.filter(
  c => !["instagram_reel", "landscape_ad"].includes(c.value)
);

// Auto-detect VideoType aus Kanal
export const autoTypeFromChannel = (ch) =>
  CHANNELS.find(c => c.value === ch)?.type || "LONGFORM";


// ── Fehler-Labels (provider-unabhängig) ──────────────────────────────────────
// Kundensichtbar → bewusst ohne Modell-/Provider-Namen (Betriebs-Interna nur im
// master-gegateten ProviderStatusPanel + docs/VIDEO_CAROUSEL_STACK_ENTSCHEIDUNG).
const ERROR_LABELS = {
  rate_limited:            "Tageslimit erreicht",
  timeout:                 "Zeitüberschreitung — bitte erneut versuchen",
  upstream_error:          "Verbindungsfehler — bitte später erneut versuchen",
  missing_config:          "Video-Erstellung ist noch nicht freigeschaltet",
  invalid_input:           "Ungültige Eingabe",
  provider_not_configured: "Kurzclips sind noch nicht freigeschaltet",
};


// ─────────────────────────────────────────────────────────────────────────────
// Sub-Komponenten
// ─────────────────────────────────────────────────────────────────────────────

// ── Status-Badge (oben rechts im Header) ─────────────────────────────────────
const ModeBadge = ({ status }) => {
  if (!status) return null;
  if (status.mode === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-700">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        Video · Live
      </span>
    );
  }
  if (status.renderer === "fal_ready") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">
        <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
        Bereit · Simulation
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      Simulationsmodus
    </span>
  );
};

// ── Master-Erkennung (fail-closed) ────────────────────────────────────────────
// Technische Betriebs-Hinweise (Modellnamen, Env-Variablen) sieht nur der Inhaber;
// Kunden bekommen das Leistungsversprechen ohne Interna.
function useIsMaster() {
  const [isMaster, setIsMaster] = useState(false);
  useEffect(() => {
    axios.get(`${API}/account/me`)
      .then((res) => setIsMaster(res.data?.is_master === true))
      .catch(() => setIsMaster(false));
  }, []);
  return isMaster;
}

// ── Provider-Status-Panel (LONGFORM + SHORTFORM) — NUR Inhaber/Master ─────────
const ProviderStatusPanel = ({ providers }) => {
  const isMaster = useIsMaster();

  if (!providers || !isMaster) return null;

  const longcat  = providers.LONGCAT  || {};
  const wan      = providers.WAN      || {};
  const seedance = providers.SEEDANCE || {};

  // SHORTFORM-Status: WAN live > WAN kein Key > Seedance stub
  let sfLabel, sfColor, sfDot;
  if (wan.configured) {
    sfLabel = "WAN 2.5 · Live";
    sfColor = "text-green-700 bg-green-50 border-green-200";
    sfDot   = "bg-green-500 animate-pulse";
  } else if (wan.has_fal_key) {
    sfLabel = "WAN 2.5 bereit · FAL_KEY gesetzt";
    sfColor = "text-sky-700 bg-sky-50 border-sky-200";
    sfDot   = "bg-sky-400";
  } else if (seedance.configured) {
    sfLabel = "Seedance · Live";
    sfColor = "text-green-700 bg-green-50 border-green-200";
    sfDot   = "bg-green-500 animate-pulse";
  } else {
    sfLabel = "SHORTFORM · nicht konfiguriert";
    sfColor = "text-stone-500 bg-stone-50 border-stone-200";
    sfDot   = "bg-stone-300";
  }

  // LONGFORM-Status
  let lfLabel, lfColor, lfDot;
  if (longcat.configured) {
    lfLabel = "Longcat 720p · Live";
    lfColor = "text-green-700 bg-green-50 border-green-200";
    lfDot   = "bg-green-500 animate-pulse";
  } else if (longcat.has_fal_key) {
    lfLabel = "Longcat · FAL_KEY gesetzt, Simulation";
    lfColor = "text-sky-700 bg-sky-50 border-sky-200";
    lfDot   = "bg-sky-400";
  } else {
    lfLabel = "Longcat · FAL_KEY fehlt";
    lfColor = "text-amber-700 bg-amber-50 border-amber-200";
    lfDot   = "bg-amber-400";
  }

  return (
    <div className="rounded-xl bg-white border border-stone-200 p-4 space-y-3">
      <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Provider-Status</h3>
      <div className="grid grid-cols-2 gap-3">
        {/* LONGFORM */}
        <div className={`rounded-lg border px-3 py-2.5 ${lfColor}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${lfDot}`} />
            <span className="text-xs font-bold uppercase tracking-wide">Longform</span>
          </div>
          <p className="text-xs font-medium">{lfLabel}</p>
          {longcat.model && (
            <p className="text-[10px] opacity-60 mt-0.5 font-mono truncate">{longcat.model.split("/").pop()}</p>
          )}
        </div>

        {/* SHORTFORM */}
        <div className={`rounded-lg border px-3 py-2.5 ${sfColor}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sfDot}`} />
            <span className="text-xs font-bold uppercase tracking-wide">Shortform</span>
          </div>
          <p className="text-xs font-medium">{sfLabel}</p>
          {wan.model && wan.configured && (
            <p className="text-[10px] opacity-60 mt-0.5 font-mono truncate">{wan.model.split("/").pop()}</p>
          )}
          {!wan.configured && !seedance.configured && (
            <p className="text-[10px] opacity-70 mt-0.5 leading-relaxed">
              FAL_KEY setzen um WAN 2.5 zu aktivieren
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Video-Typ Badge ───────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  if (!type) return null;
  const isLong = type === "LONGFORM";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
      isLong ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
    }`}>
      {isLong ? "🎬 LONGFORM" : "📱 SHORTFORM"}
    </span>
  );
};

// ── Video-Ergebnis Karte ──────────────────────────────────────────────────────
const VideoResultCard = ({ result, onRetry }) => {
  if (!result) return null;

  const isSimulated = result.simulated || result.status === "simulated";
  const isLive      = result.mode === "live" || result.status === "completed";

  if (!result.success) {
    const isNotConfigured = result.error === "provider_not_configured";
    const errorLabel = ERROR_LABELS[result.error] || "Generierung fehlgeschlagen";

    if (isNotConfigured) {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 text-sm">{errorLabel}</p>
              <p className="text-sm text-amber-700 mt-1">{result.message}</p>
              <p className="text-xs text-amber-600 mt-2 leading-relaxed">
                Die Kurzclip-Erstellung ist gerade nicht verfügbar — wir schalten sie zentral frei.
                {" "}Wähle einen <strong>LONGFORM-Kanal</strong> (LinkedIn, Facebook, YouTube, Blog) als Alternative.
              </p>
            </div>
          </div>
          {onRetry && (
            <button onClick={onRetry}
              className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors">
              <RefreshCw size={12} />
              Anderen Kanal wählen
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 text-sm">{errorLabel}</p>
            <p className="text-sm text-red-600 mt-1">{result.message}</p>
          </div>
        </div>
        {onRetry && (
          <button onClick={onRetry}
            className="flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors">
            <RefreshCw size={12} />
            Erneut versuchen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-5 space-y-4 ${
      isSimulated ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"
    }`}>
      {/* Titel + Modus */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isSimulated
            ? <Info size={18} className="text-amber-600 flex-shrink-0" />
            : <CheckCircle size={18} className="text-green-600 flex-shrink-0" />}
          <p className={`font-semibold text-sm ${isSimulated ? "text-amber-800" : "text-green-800"}`}>
            {isSimulated ? "Video simuliert — kein echter Clip erstellt" : "Video erfolgreich generiert!"}
          </p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
          isSimulated ? "bg-amber-200 text-amber-800" : "bg-green-200 text-green-800"
        }`}>
          {isSimulated ? "SIMULATION" : "LIVE"}
        </span>
      </div>

      {isSimulated && (
        <p className="text-xs text-amber-700 leading-relaxed">
          Im Simulationsmodus wird kein echtes Video erstellt — die URL ist ein Platzhalter.
          Die echte Video-Erstellung wird zentral freigeschaltet.
        </p>
      )}

      {/* Video Player (nur Live) */}
      {isLive && result.video_url && (
        <div className="space-y-3">
          <div className="relative bg-black rounded-xl overflow-hidden aspect-video flex items-center justify-center">
            <div className="text-center text-white">
              <Play size={48} className="mx-auto mb-2 opacity-80" />
              <p className="text-sm opacity-70">Video bereit</p>
            </div>
          </div>
          <a href={result.video_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-800">
            <ExternalLink size={14} />
            Video öffnen
          </a>
        </div>
      )}

      {/* Simulated URL */}
      {isSimulated && result.video_url && (
        <p className="text-xs font-mono text-amber-600 bg-amber-100 rounded px-2 py-1 break-all">
          {result.video_url}
        </p>
      )}

      {/* Metadaten */}
      <div className="text-xs text-stone-500 space-y-1 pt-1 border-t border-stone-200/50">
        <div className="flex items-center gap-2 flex-wrap">
          {result.type && <TypeBadge type={result.type} />}
          {result.provider && result.provider !== "NONE" && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
              {result.provider}
            </span>
          )}
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            result.mode === "live" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
          }`}>
            {result.mode === "live" ? "Live" : "Simulation"}
          </span>
        </div>
        <p>Kanal: <span className="font-medium text-stone-700">{result.channel || "—"}</span></p>
        <p>Thema: <span className="font-medium text-stone-700">{result.topic || "—"}</span></p>
        {result.duration_seconds != null && (
          <p>Generierungszeit:{" "}
            <span className="font-medium text-stone-700">{result.duration_seconds.toFixed(1)}s</span>
          </p>
        )}
        {result.fal_request_id && (
          <p className="font-mono text-stone-400">Request-ID: {result.fal_request_id}</p>
        )}
      </div>
    </div>
  );
};


// ── Batch Panel ───────────────────────────────────────────────────────────────

// Helper: video_status → Badge-Farbe + Label
const VideoStatusBadge = ({ vs }) => {
  if (!vs) return null;
  const map = {
    ready:      { cls: "bg-green-100 text-green-700",  label: "✓ Video" },
    generating: { cls: "bg-blue-100 text-blue-600",    label: "⟳ läuft" },
    failed:     { cls: "bg-red-100 text-red-600",      label: "✗ fehler" },
    pending:    { cls: "bg-stone-100 text-stone-500",  label: "· offen" },
  };
  const { cls, label } = map[vs] || { cls: "bg-stone-100 text-stone-400", label: vs };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
  );
};

// Helper: platform → Icon
const platformIcon = (p) => {
  const m = { tiktok: "📱", instagram: "📸", instagram_reel: "📸",
              youtube_short: "▶️", linkedin: "💼", facebook: "👥",
              youtube: "▶️", blog: "📝", landscape_ad: "🖥️" };
  return m[p] || "📄";
};

const BatchPanel = () => {
  // Mode: "select" (Post-Picker) | "manual" (Textarea-Fallback)
  const [mode, setMode]                 = useState("select");

  // Post-Picker State
  const [posts, setPosts]               = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError]     = useState(null);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [searchQuery, setSearchQuery]   = useState("");

  const now = new Date();
  const [planMonth, setPlanMonth]       = useState(now.getMonth() + 1); // 1-12
  const [planYear, setPlanYear]         = useState(now.getFullYear());

  // Manual-Mode State
  const [postIdsText, setPostIdsText]   = useState("");

  // Shared State
  const [batchType, setBatchType]       = useState("LONGFORM");
  const [confirmCost, setConfirmCost]   = useState(false);
  const [loading, setLoading]           = useState(false);
  const [batchResult, setBatchResult]   = useState(null);
  const [batchError, setBatchError]     = useState(null);

  // ── Post-Picker laden ─────────────────────────────────────────────────────
  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    setPostsError(null);
    try {
      const res = await axios.get(`${API}/monthly-plan/posts`, {
        params: { month: planMonth, year: planYear, limit: 100 },
      });
      // API gibt entweder { posts: [...] } oder direkt Array zurück
      const data = Array.isArray(res.data) ? res.data : (res.data.posts || []);
      setPosts(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Posts konnten nicht geladen werden.";
      setPostsError(msg);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [planMonth, planYear]);

  useEffect(() => {
    if (mode === "select") loadPosts();
  }, [loadPosts, mode]);

  // ── Monat navigieren ──────────────────────────────────────────────────────
  const prevMonth = () => {
    if (planMonth === 1) { setPlanMonth(12); setPlanYear(y => y - 1); }
    else setPlanMonth(m => m - 1);
    setSelectedIds(new Set());
  };
  const nextMonth = () => {
    if (planMonth === 12) { setPlanMonth(1); setPlanYear(y => y + 1); }
    else setPlanMonth(m => m + 1);
    setSelectedIds(new Set());
  };

  const monthLabel = new Date(planYear, planMonth - 1, 1)
    .toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  // ── Suche ─────────────────────────────────────────────────────────────────
  const filteredPosts = posts.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.hook  || "").toLowerCase().includes(q) ||
      (p.theme || "").toLowerCase().includes(q) ||
      (p.platform || "").toLowerCase().includes(q)
    );
  });

  // ── Auswahl-Helpers ───────────────────────────────────────────────────────
  const MAX_SELECT = 20;
  const togglePost = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else if (next.size < MAX_SELECT) { next.add(id); }
      else { toast.warning(`Maximal ${MAX_SELECT} Posts auswählbar`); }
      return next;
    });
  };
  const selectAll = () => {
    const ids = filteredPosts.slice(0, MAX_SELECT).map(p => p.id).filter(Boolean);
    setSelectedIds(new Set(ids));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // ── Aktive Post-IDs (je nach Modus) ──────────────────────────────────────
  const manualIds = postIdsText
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean);

  const activeIds = mode === "select" ? [...selectedIds] : manualIds;

  // ── Batch abschicken ──────────────────────────────────────────────────────
  const handleBatch = async () => {
    if (activeIds.length === 0) {
      toast.error(mode === "select"
        ? "Bitte mindestens einen Post auswählen"
        : "Bitte mindestens eine Post-ID eingeben");
      return;
    }
    if (!confirmCost) {
      toast.error("Bitte bestätige die Kosten, bevor du den Batch startest");
      return;
    }

    setLoading(true);
    setBatchResult(null);
    setBatchError(null);

    try {
      const res = await axios.post(`${API}/video/generate-batch`, {
        post_ids:     activeIds,
        confirm_cost: confirmCost,
        type:         batchType,
      });
      setBatchResult(res.data);

      if (res.data.status === "confirmation_required") {
        toast.warning("confirm_cost=true ist erforderlich");
      } else if (res.data.status === "insufficient_quota") {
        toast.error("Nicht genug Quota für diesen Batch");
      } else {
        const s = res.data.summary;
        toast.success(`Batch fertig: ${s?.successful || 0} ✓  ${s?.failed || 0} ✗`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Batch-Fehler";
      setBatchError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Fehlgeschlagene aus Ergebnis erneut laden ─────────────────────────────
  const retryFailed = () => {
    if (!batchResult?.results) return;
    const failedIds = batchResult.results
      .filter(r => !r.success)
      .map(r => r.post_id)
      .filter(Boolean);

    if (mode === "select") {
      setSelectedIds(new Set(failedIds));
    } else {
      setPostIdsText(failedIds.join("\n"));
    }
    setBatchResult(null);
    setConfirmCost(false);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-5">

        {/* Header + Modus-Toggle */}
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-stone-800 flex items-center gap-2">
            <List size={16} className="text-fox-fire" />
            Batch-Generierung
          </h2>
          <div className="inline-flex rounded-lg border border-stone-200 bg-stone-50 p-0.5 gap-0.5">
            <button
              type="button"
              onClick={() => setMode("select")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                mode === "select"
                  ? "bg-white border border-stone-200 text-stone-800 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Calendar size={12} />
                Monatsplan
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMode("manual")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                mode === "manual"
                  ? "bg-white border border-stone-200 text-stone-800 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <span className="flex items-center gap-1.5">
                Manuell
              </span>
            </button>
          </div>
        </div>

        {/* ── Post-Picker (select-Modus) ── */}
        {mode === "select" && (
          <div className="space-y-3">
            {/* Monat-Navigation */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={prevMonth}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm font-semibold text-stone-700">{monthLabel}</span>
              <button
                type="button"
                onClick={nextMonth}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Suche + Auswahl-Controls */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Suchen…"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
                />
              </div>
              <button
                type="button"
                onClick={selectAll}
                disabled={filteredPosts.length === 0}
                className="px-3 py-2 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors disabled:opacity-40"
              >
                Alle
              </button>
              <button
                type="button"
                onClick={clearSelection}
                disabled={selectedIds.size === 0}
                className="px-3 py-2 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors disabled:opacity-40"
              >
                Leeren
              </button>
            </div>

            {/* Ausgewählt-Counter */}
            <p className="text-xs text-stone-500">
              {selectedIds.size > 0
                ? <><strong>{selectedIds.size}</strong> von {MAX_SELECT} ausgewählt</>
                : "Noch keine Posts ausgewählt"}
              {selectedIds.size === MAX_SELECT && (
                <span className="ml-1.5 text-amber-600 font-semibold">· Maximum erreicht</span>
              )}
            </p>

            {/* Post-Liste */}
            {postsLoading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-fox-fire" />
              </div>
            )}

            {postsError && !postsLoading && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1.5">
                  <p className="text-sm text-red-700">{postsError}</p>
                  <button
                    type="button"
                    onClick={loadPosts}
                    className="text-xs text-red-600 hover:text-red-800 underline underline-offset-2"
                  >
                    Erneut versuchen
                  </button>
                </div>
              </div>
            )}

            {!postsLoading && !postsError && posts.length === 0 && (
              <div className="text-center py-10 text-stone-400">
                <Calendar size={28} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Keine Posts für {monthLabel}</p>
                <p className="text-xs mt-1 opacity-70">Monat wechseln oder Manuell-Modus nutzen</p>
              </div>
            )}

            {!postsLoading && filteredPosts.length === 0 && posts.length > 0 && (
              <div className="text-center py-6 text-stone-400 text-sm">
                Keine Posts für „{searchQuery}"
              </div>
            )}

            {!postsLoading && filteredPosts.length > 0 && (
              <div className="border border-stone-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                {filteredPosts.map((post, i) => {
                  const isSelected = selectedIds.has(post.id);
                  const atMax = selectedIds.size >= MAX_SELECT && !isSelected;
                  return (
                    <label
                      key={post.id || i}
                      className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-stone-100 last:border-b-0 ${
                        isSelected
                          ? "bg-orange-50"
                          : atMax
                            ? "opacity-40 cursor-not-allowed bg-white"
                            : "bg-white hover:bg-stone-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={atMax}
                        onChange={() => !atMax && togglePost(post.id)}
                        className="mt-0.5 h-4 w-4 rounded border-stone-300 text-fox-fire focus:ring-fox-fire flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-stone-700 truncate leading-snug">
                          {post.hook || <span className="text-stone-400 italic">Kein Titel</span>}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {post.platform && (
                            <span className="text-xs text-stone-500">
                              {platformIcon(post.platform)} {post.platform}
                            </span>
                          )}
                          {post.theme && (
                            <span className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                              {post.theme}
                            </span>
                          )}
                          {post.scheduled_at && (
                            <span className="text-[10px] text-stone-400">
                              {new Date(post.scheduled_at).toLocaleDateString("de-DE", { day: "numeric", month: "short" })}
                            </span>
                          )}
                          <VideoStatusBadge vs={post.video_status} />
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Manueller Modus (Textarea-Fallback) ── */}
        {mode === "manual" && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">
              Post-IDs{" "}
              <span className="text-stone-400 font-normal">(eine pro Zeile, max. 20)</span>
            </label>
            <textarea
              value={postIdsText}
              onChange={e => setPostIdsText(e.target.value)}
              placeholder={"post-abc123\npost-def456\npost-ghi789"}
              rows={6}
              className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire resize-none"
            />
            <p className="text-xs text-stone-400 mt-1">
              {manualIds.length} Post{manualIds.length !== 1 ? "s" : ""} erkannt
              {manualIds.length > 20 && (
                <span className="text-red-500 font-semibold"> — maximal 20 erlaubt</span>
              )}
            </p>
          </div>
        )}

        {/* Video-Typ */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Video-Typ
          </label>
          <div className="flex gap-2">
            {["LONGFORM", "SHORTFORM"].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setBatchType(t)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  batchType === t
                    ? t === "LONGFORM"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-purple-50 border-purple-300 text-purple-700"
                    : "bg-white border-stone-200 text-stone-500 hover:border-stone-300"
                }`}
              >
                {t === "LONGFORM" ? "🎬 Longform" : "📱 Shortform"}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setBatchType(null)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                batchType === null
                  ? "bg-stone-100 border-stone-400 text-stone-700"
                  : "bg-white border-stone-200 text-stone-500 hover:border-stone-300"
              }`}
            >
              Auto (je Kanal)
            </button>
          </div>
        </div>

        {/* Kosten bestätigen */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmCost}
              onChange={e => setConfirmCost(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-stone-300 text-fox-fire focus:ring-fox-fire"
            />
            <span className="text-sm text-amber-800">
              <strong>Ich bestätige die Kosten.</strong>{" "}
              Jedes echte Video verursacht Generierungskosten. Batch mit{" "}
              <strong>{activeIds.length}</strong> Post{activeIds.length !== 1 ? "s" : ""} starten.
            </span>
          </label>
        </div>

        {/* Action */}
        <button
          onClick={handleBatch}
          disabled={loading || activeIds.length === 0 || activeIds.length > 20}
          className="flex items-center gap-2 px-5 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" />Batch läuft…</>
          ) : (
            <><Zap size={16} />Batch starten ({activeIds.length} Videos)</>
          )}
        </button>
      </div>

      {/* Batch-Fehler */}
      {batchError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{batchError}</p>
        </div>
      )}

      {/* Batch-Ergebnis */}
      {batchResult && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
          {/* Summary */}
          {batchResult.summary && (
            <div className="flex items-center gap-4 pb-3 border-b border-stone-100">
              <div className="text-center">
                <p className="text-2xl font-bold text-stone-800">{batchResult.summary.total}</p>
                <p className="text-xs text-stone-400">Gesamt</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{batchResult.summary.successful}</p>
                <p className="text-xs text-stone-400">Erfolgreich</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">{batchResult.summary.failed}</p>
                <p className="text-xs text-stone-400">Fehlgeschlagen</p>
              </div>
            </div>
          )}

          {/* Status-Meldungen ohne Ergebnis-Liste */}
          {batchResult.status === "confirmation_required" && (
            <div className="flex items-center gap-2 text-amber-700 text-sm">
              <Info size={14} />
              Bitte bestätige die Kosten und sende erneut.
            </div>
          )}
          {batchResult.status === "insufficient_quota" && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={14} />
              {batchResult.message}
            </div>
          )}

          {/* Einzel-Ergebnisse */}
          {batchResult.results && batchResult.results.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Ergebnisse</h3>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {batchResult.results.map((r, i) => (
                  <div key={i} className={`rounded-lg border px-3 py-2.5 text-sm flex items-center justify-between gap-3 ${
                    r.success
                      ? "border-green-200 bg-green-50"
                      : r.status === "not_found"
                        ? "border-stone-200 bg-stone-50"
                        : "border-red-200 bg-red-50"
                  }`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {r.success
                        ? <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        : <AlertCircle size={14} className="text-red-500 flex-shrink-0" />}
                      {/* In Select-Modus: Hook anzeigen wenn verfügbar */}
                      {mode === "select" ? (() => {
                        const p = posts.find(p => p.id === r.post_id);
                        return (
                          <span className="text-xs truncate text-stone-600">
                            {p?.hook || r.post_id}
                          </span>
                        );
                      })() : (
                        <span className="font-mono text-xs truncate text-stone-600">{r.post_id}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {r.success && r.type && <TypeBadge type={r.type} />}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        r.success
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {r.success ? (r.simulated ? "simuliert" : "generiert") : (r.status || "fehler")}
                      </span>
                      {r.success && r.video_url && (
                        <a href={r.video_url} target="_blank" rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Fehlgeschlagene erneut versuchen */}
              {batchResult.summary?.failed > 0 && (
                <button
                  type="button"
                  onClick={retryFailed}
                  className="text-xs text-amber-600 hover:text-amber-800 underline underline-offset-2"
                >
                  Fehlgeschlagene erneut versuchen ({batchResult.summary.failed})
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ── Logs Panel ────────────────────────────────────────────────────────────────
const LogsPanel = ({ refreshToken = 0 }) => {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);
  const [limit, setLimit]       = useState(20);
  const [error, setError]       = useState(null);

  const fetchLogs = useCallback(async (newLimit = limit) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/video/logs`, { params: { limit: newLimit } });
      setLogs(res.data.logs || []);
      setLoaded(true);
    } catch (err) {
      setError("Logs konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Initialer Fetch beim Mounten
  useEffect(() => {
    fetchLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh wenn von VideoPage ausgelöst (nach erfolgreicher Generierung)
  useEffect(() => {
    if (refreshToken > 0) fetchLogs();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const next = limit + 20;
    setLimit(next);
    fetchLogs(next);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-stone-800 flex items-center gap-2">
          <Clock size={16} className="text-fox-fire" />
          Video-Logs
          {loaded && (
            <span className="text-xs text-stone-400 font-normal ml-1">
              ({logs.length} Einträge)
            </span>
          )}
        </h2>
        <button
          onClick={() => fetchLogs(limit)}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-lg transition-colors"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Aktualisieren
        </button>
      </div>

      {/* Fehler */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle size={14} className="text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Ladezustand */}
      {loading && !loaded && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-fox-fire" />
        </div>
      )}

      {/* Leer */}
      {loaded && logs.length === 0 && (
        <div className="text-center py-12 text-stone-400">
          <FileVideo size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">Noch keine Video-Generierungen</p>
        </div>
      )}

      {/* Tabelle */}
      {logs.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500 whitespace-nowrap">Datum/Zeit</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500">Provider</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500">Typ</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500">Modus</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500">Kanal</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-stone-500">Thema</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-stone-500 whitespace-nowrap">Zeit (s)</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-stone-500">Video</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {logs.map((log, i) => (
                  <tr key={i} className="hover:bg-stone-50 transition-colors">
                    <td className="px-3 py-2.5 whitespace-nowrap text-stone-500 font-mono">
                      {fmt(log.timestamp)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`font-semibold px-1.5 py-0.5 rounded text-[10px] ${
                        log.provider === "WAN"
                          ? "bg-purple-100 text-purple-700"
                          : log.provider === "LONGCAT"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-stone-100 text-stone-600"
                      }`}>
                        {log.provider === "WAN" ? "Kurzclip"
                          : log.provider === "LONGCAT" ? "Video"
                          : (log.provider || "—")}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {log.video_type && <TypeBadge type={log.video_type} />}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        log.mode === "live"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {log.mode || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-stone-600">{log.channel || "—"}</td>
                    <td className="px-3 py-2.5 max-w-[140px]">
                      <span className="truncate block text-stone-600" title={log.prompt_preview || log.topic}>
                        {log.prompt_preview || log.topic || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-stone-500 whitespace-nowrap">
                      {log.duration_seconds != null ? log.duration_seconds.toFixed(1) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {log.video_url ? (
                        <a href={log.video_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-green-600 hover:text-green-700">
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {logs.length >= limit && (
            <div className="border-t border-stone-100 px-4 py-3 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="flex items-center gap-1.5 mx-auto text-xs text-stone-500 hover:text-stone-700 font-medium"
              >
                {loading
                  ? <><Loader2 size={12} className="animate-spin" />Lädt…</>
                  : <><ArrowRight size={12} />Mehr laden</>}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Hauptkomponente
// ─────────────────────────────────────────────────────────────────────────────

const VideoPage = () => {
  const location = useLocation();

  // Status & Provider
  const [status, setStatus]           = useState(null);
  const [providers, setProviders]     = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);
  // Betriebs-Hinweise (Env-/Modell-Details aus status.hint) nur für den Inhaber
  const isMaster = useIsMaster();

  // Tab
  const [activeTab, setActiveTab]     = useState("generate");

  // Einzelgenerator
  const [topic, setTopic]             = useState("");
  const [text, setText]               = useState("");
  const [channel, setChannel]         = useState("instagram");
  const [videoType, setVideoType]     = useState(autoTypeFromChannel("instagram"));
  const [typeOverride, setTypeOverride] = useState(false);
  const [imageIdea, setImageIdea]     = useState("");
  const [generating, setGenerating]   = useState(false);
  const [result, setResult]           = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [logsRefreshToken, setLogsRefreshToken] = useState(0);
  // post_id aus Monthly Plan — wenn gesetzt, wird der Writeback den richtigen Post treffen
  const [prefillPostId, setPrefillPostId] = useState(null);
  // Double-Click-Schutz: verhindert parallele Fal-Requests
  const inflightRef = useRef(false);
  // Abort-Handle: ermöglicht Request-Abbruch bei Unmount / Navigation weg
  const abortControllerRef = useRef(null);

  // Cleanup bei Unmount: laufenden Fal-Request abbrechen
  // Verhindert stale toast + erzwungene Rücknavigation wenn User wegnavigiert.
  useEffect(() => () => { abortControllerRef.current?.abort(); }, []);

  // ── Prefill aus Monthly Plan (router state) ──────────────────────────────
  // MonthlyPlanPage navigiert mit: navigate('/video', { state: { post_id, topic, text, channel, image_idea, video_type } })
  useEffect(() => {
    const s = location.state;
    if (!s) return;
    if (s.post_id)    setPrefillPostId(s.post_id);
    if (s.topic)      setTopic(s.topic);
    if (s.text)       setText(s.text);
    if (s.image_idea) setImageIdea(s.image_idea);
    if (s.channel) {
      const ch = s.channel;
      setChannel(ch);
      // video_type überschreibt Auto-Detect nur wenn explizit mitgegeben
      const vt = s.video_type || autoTypeFromChannel(ch);
      setVideoType(vt);
      setTypeOverride(!!s.video_type && s.video_type !== autoTypeFromChannel(ch));
    }
    // Hinweis-Toast damit Nutzer weiß, was passiert ist
    if (s.topic || s.channel) {
      toast.info("Daten aus dem Monatsplan übernommen");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Status + Provider laden
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/video/status`).catch(() => null),
      axios.get(`${API}/video/providers`).catch(() => null),
    ]).then(([statusRes, providersRes]) => {
      if (statusRes)    setStatus(statusRes.data);
      if (providersRes) setProviders(providersRes.data.providers);
      // Wenn beide Fetches fehlschlagen → Fehler anzeigen
      if (!statusRes && !providersRes) {
        setStatusError("Backend nicht erreichbar — bitte neu laden.");
      }
    }).finally(() => setLoadingStatus(false));
  }, []);

  // SHORTFORM live-Status aus providers ableiten
  const wanIsLive = providers?.WAN?.configured === true;
  const shortformReady = wanIsLive || providers?.SEEDANCE?.configured === true;

  const handleGenerate = async () => {
    // ── C) Double-Click-Schutz ────────────────────────────────────────────────
    // inflightRef verhindert parallele Requests auch wenn React den State noch
    // nicht synchron aktualisiert hat (relevanter Edge-Case bei schnellem Klick).
    if (inflightRef.current || generating) return;
    if (!topic.trim()) { toast.error("Bitte gib ein Thema ein"); return; }

    inflightRef.current = true;
    setGenerating(true);
    setResult(null);

    // Neuer AbortController pro Request — vorherigen ggf. abbrechen
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Wenn aus MonthlyPlanPage navigiert: echte post_id verwenden, damit
    // der Backend-Writeback video_status im richtigen Plan-Post aktualisiert.
    const postId = prefillPostId || `video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      // ── C) Axios-Timeout (4 Min) + AbortController ───────────────────────
      // timeout: Fal.ai antwortet nicht innerhalb von 4 Min → ECONNABORTED
      // signal:  User navigiert weg → abort() → ERR_CANCELED (kein stale toast,
      //          keine erzwungene Rücknavigation)
      const res = await axios.post(`${API}/video/generate`, {
        post_id:    postId,
        topic:      topic.trim(),
        text:       text.trim(),
        channel,
        image_idea: imageIdea.trim() || null,
        type:       videoType,
      }, { timeout: 240_000, signal: abortControllerRef.current.signal }); // 4 Minuten

      setResult(res.data);

      if (res.data.success) {
        toast.success(res.data.simulated ? "Video simuliert" : "Video generiert!");
        setLogsRefreshToken(t => t + 1);

        // ── A) Rücknavigation zum Monatsplan ─────────────────────────────
        // Nur wenn wir aus dem Plan kamen (prefillPostId gesetzt).
        // 900ms Pause damit der Toast lesbar ist, bevor die Seite wechselt.
        if (prefillPostId) {
          setTimeout(() => {
            navigate('/monthly-plan', {
              state: { refreshPlan: true, updatedPostId: prefillPostId },
            });
          }, 900);
        }
      } else {
        toast.error(res.data.message || "Generierung fehlgeschlagen");
      }
    } catch (err) {
      // Unmount-Abbruch: kein toast, keine Navigation, kein setState
      if (err.code === "ERR_CANCELED") return;

      let msg, errorType;
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        // axios-Timeout (240s überschritten — Fal.ai hat nicht geantwortet)
        errorType = "timeout";
        msg = "Timeout nach 4 Minuten — Fal.ai hat nicht geantwortet. Bitte erneut versuchen.";
      } else if (!err.response) {
        errorType = "upstream_error";
        msg = "Backend nicht erreichbar — starte das Backend neu.";
      } else if (err.response.status === 422) {
        errorType = "invalid_input";
        const detail = err.response.data?.detail;
        msg = Array.isArray(detail)
          ? detail.map(d => d.msg || d).join(", ")
          : (detail || "Ungültige Eingabe — prüfe Thema und Kanal.");
      } else if (err.response.status === 429) {
        errorType = "rate_limited";
        msg = err.response.data?.detail || "Tageslimit erreicht.";
      } else if (err.response.status === 500) {
        errorType = "upstream_error";
        msg = err.response.data?.detail || "Interner Fehler — prüfe das Backend-Log.";
      } else {
        errorType = "upstream_error";
        msg = err.response.data?.detail || `Fehler ${err.response.status}`;
      }
      toast.error(msg.split("\n")[0]);
      setResult({ success: false, status: "error", error: errorType, message: msg });
    } finally {
      setGenerating(false);
      inflightRef.current = false; // Semaphor freigeben
    }
  };

  // Formular vollständig zurücksetzen
  const handleReset = () => {
    setResult(null);
    setTopic("");
    setText("");
    setImageIdea("");
    setChannel("instagram");
    setVideoType(autoTypeFromChannel("instagram"));
    setTypeOverride(false);
  };

  // Nur Ergebnis löschen und denselben Request erneut abschicken
  const handleRetry = () => {
    setResult(null);
    // Kurzes Timeout damit React den State flusht, bevor handleGenerate läuft
    setTimeout(handleGenerate, 0);
  };

  const handleChannelChange = (val) => {
    setChannel(val);
    if (!typeOverride) setVideoType(autoTypeFromChannel(val));
  };

  // ── Tab-Konfiguration ─────────────────────────────────────────────────────
  // Batch-Tab hinter Flag (22.07.): synchron-sequenzieller 20er-Lauf in EINEM Request
  // — Browser/Proxy kappen nach Minuten (s. config/features.js VIDEO_BATCH_ENABLED).
  const TABS = [
    { id: "generate", label: "Video erstellen", icon: <Video size={14} /> },
    ...(VIDEO_BATCH_ENABLED ? [{ id: "batch", label: "Batch", icon: <List size={14} /> }] : []),
    { id: "logs",     label: "Logs",            icon: <Clock size={14} /> },
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-fox-fire rounded-xl flex items-center justify-center flex-shrink-0">
              <Video size={20} className="text-white" />
            </div>
            Video erstellen
          </h1>
          <p className="text-stone-500 mt-1 ml-14">
            Erstelle kurze Social Media Videos aus deinem Content.
          </p>
        </div>
        {!loadingStatus && <ModeBadge status={status} />}
      </div>

      {/* ── Backend-Fehler-Banner ──────────────────────────────────────── */}
      {statusError && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800">{statusError}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex-shrink-0 text-xs font-semibold text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors"
          >
            Neu laden
          </button>
        </div>
      )}

      {/* ── Provider-Status Panel ──────────────────────────────────────── */}
      {!loadingStatus && providers && (
        <ProviderStatusPanel providers={providers} />
      )}

      {/* ── Status-Hinweise (Simulation / Live / Config) ───────────────── */}
      {!loadingStatus && status && (
        <>
          {status.mode === "live" && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-start gap-3">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-semibold">Live-Modus aktiv</p>
                <p className="text-green-700">Generierung erzeugt echte Videos.</p>
              </div>
            </div>
          )}
          {status.mode === "simulation" && status.renderer === "fal_ready" && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 flex items-start gap-3">
              <Info size={16} className="text-sky-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-sky-800 space-y-1">
                <p className="font-semibold">Video-Anbindung bereit — Simulation aktiv</p>
                <p className="text-sky-700">{isMaster ? status.hint
                  : "Die echte Video-Erstellung wird zentral freigeschaltet — probiere alles im Simulationsmodus aus."}</p>
              </div>
            </div>
          )}
          {status.mode === "simulation" && status.renderer !== "fal_ready" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
              <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 space-y-1">
                <p className="font-semibold">Simulationsmodus</p>
                <p className="text-amber-700">{isMaster ? status.hint
                  : "Die echte Video-Erstellung wird zentral freigeschaltet — probiere alles im Simulationsmodus aus."}</p>
              </div>
            </div>
          )}
          {!status.can_generate && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
              <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 font-medium">
                Tageslimit erreicht — heute sind keine weiteren Videos möglich.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Tageslimit ─────────────────────────────────────────────────── */}
      {status?.usage && (
        <div className="rounded-xl bg-white border border-stone-200 p-4 flex items-center justify-between">
          <div className="text-sm text-stone-600">
            <span className="font-semibold text-stone-800">{status.usage.remaining}</span>
            {" "}von{" "}
            <span className="font-semibold text-stone-800">{status.usage.limit}</span>
            {" "}Videos heute noch verfügbar
          </div>
          <div className="flex gap-1">
            {Array.from({ length: status.usage.limit }).map((_, i) => (
              <div key={i}
                className={`w-2 h-2 rounded-full ${
                  i < status.usage.videos_generated ? "bg-fox-fire" : "bg-stone-200"
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-xl">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === tab.id
                ? "bg-white text-stone-800 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Video erstellen ────────────────────────────────────────── */}
      {activeTab === "generate" && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-5">
            <h2 className="font-semibold text-stone-800 flex items-center gap-2">
              <Sparkles size={16} className="text-fox-fire" />
              Video-Details
            </h2>

            {/* Thema */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Thema <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder="z. B. 5 Tipps zur Förderantrag-Vorbereitung"
                maxLength={200}
                className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
              />
              <p className="text-xs text-stone-400 mt-1">{topic.length}/200</p>
            </div>

            {/* Kanal */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Ziel-Kanal
              </label>
              <select
                value={channel}
                onChange={e => handleChannelChange(e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
              >
                <optgroup label="📱 Kurzclip (SHORTFORM)">
                  {CHANNELS_MAIN.filter(c => c.type === "SHORTFORM").map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}{c.release === "prepared" ? " — in Vorbereitung" : ""}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="🎬 Langes Video (LONGFORM)">
                  {CHANNELS_MAIN.filter(c => c.type === "LONGFORM").map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}{c.release === "prepared" ? " — in Vorbereitung" : ""}
                    </option>
                  ))}
                </optgroup>
              </select>

              {/* Hinweis für "prepared" Kanäle — keine UX-Lüge, klarer Erwartungshorizont */}
              {CHANNELS_MAIN.find(c => c.value === channel)?.release === "prepared" && (
                <div className="mt-2 px-3 py-2 rounded-md bg-stone-50 border border-stone-200 text-xs text-stone-600 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 shrink-0 text-stone-400" />
                  <div>
                    <span className="font-medium text-stone-700">Dieser Kanal wird gerade vorbereitet.</span>{" "}
                    Du kannst Inhalte erstellen und als Entwurf speichern. Die automatische
                    Veröffentlichung ist noch nicht freigeschaltet.
                  </div>
                </div>
              )}

              {/* Video-Typ Segmented Control */}
              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-stone-500">Video-Typ:</span>
                <div className="inline-flex rounded-full border border-stone-200 bg-stone-100 p-0.5 gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setVideoType("LONGFORM");
                      setTypeOverride("LONGFORM" !== autoTypeFromChannel(channel));
                    }}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                      videoType === "LONGFORM"
                        ? "bg-blue-50 border border-blue-200 text-blue-700 shadow-sm"
                        : "text-stone-500 hover:text-stone-700"
                    }`}
                  >
                    🎬 Langes Video
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setVideoType("SHORTFORM");
                      setTypeOverride("SHORTFORM" !== autoTypeFromChannel(channel));
                    }}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                      videoType === "SHORTFORM"
                        ? "bg-purple-50 border border-purple-200 text-purple-700 shadow-sm"
                        : "text-stone-500 hover:text-stone-700"
                    }`}
                  >
                    📱 Kurzclip
                  </button>
                </div>
                {typeOverride && (
                  <button
                    type="button"
                    onClick={() => { setVideoType(autoTypeFromChannel(channel)); setTypeOverride(false); }}
                    className="text-xs text-stone-400 hover:text-stone-600 underline"
                  >
                    zurücksetzen
                  </button>
                )}
              </div>

              {/* SHORTFORM-Hinweis: dynamisch je nach WAN-Status */}
              {videoType === "SHORTFORM" && (
                <div className={`mt-2 rounded-lg border px-3 py-2.5 flex items-start gap-2 ${
                  shortformReady
                    ? "border-green-200 bg-green-50"
                    : "border-amber-200 bg-amber-50"
                }`}>
                  {shortformReady
                    ? <CheckCircle size={13} className="text-green-600 flex-shrink-0 mt-0.5" />
                    : <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />}
                  <p className={`text-xs leading-relaxed ${shortformReady ? "text-green-700" : "text-amber-700"}`}>
                    {wanIsLive
                      ? <><strong>Kurzclips sind aktiv.</strong> 9:16-Clips für TikTok, Instagram &amp; Shorts werden automatisch erstellt.</>
                      : shortformReady
                        ? <><strong>Kurzclips sind bereit.</strong></>
                        : <><strong>Kurzclips sind noch nicht freigeschaltet.</strong>{" "}
                            Wir schalten sie zentral frei — wähle solange einen{" "}
                            <strong>LONGFORM-Kanal</strong> (LinkedIn, Facebook, YouTube, Blog).</>
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Erweitert */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
              >
                <ChevronDown size={14} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                Erweiterte Optionen
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      Post-Text (optional)
                    </label>
                    <textarea
                      value={text}
                      onChange={e => setText(e.target.value)}
                      placeholder="Füge deinen fertigen Post-Text hier ein — hilft bei der Video-Generierung"
                      rows={4}
                      maxLength={1000}
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire resize-none"
                    />
                    <p className="text-xs text-stone-400 mt-1">{text.length}/1000</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      Bild-/Stil-Idee (optional)
                    </label>
                    <input
                      type="text"
                      value={imageIdea}
                      onChange={e => setImageIdea(e.target.value)}
                      placeholder="z. B. professionell, heller Hintergrund, Schreibtisch"
                      maxLength={200}
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleGenerate}
                disabled={generating || !topic.trim() || (status?.usage?.remaining === 0)}
                aria-busy={generating}
                className="flex items-center gap-2 px-5 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <><Loader2 size={16} className="animate-spin" />Wird erstellt…</>
                ) : (
                  <><Video size={16} />Video erstellen</>
                )}
              </button>

              {result && (
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-2.5 text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-xl font-medium text-sm transition-colors"
                >
                  <RefreshCw size={14} />
                  Neu starten
                </button>
              )}
            </div>

            {status?.usage?.remaining === 0 && (
              <p className="text-sm text-red-600 flex items-center gap-1.5">
                <AlertCircle size={14} />
                Tageslimit erreicht — morgen wieder verfügbar
              </p>
            )}
          </div>

          {/* Ergebnis */}
          {result && (
            <VideoResultCard
              result={result}
              onRetry={result.success ? null : handleRetry}
            />
          )}

          {/* Info-Box */}
          {!result && (
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-5">
              <h3 className="font-semibold text-stone-700 text-sm mb-3 flex items-center gap-2">
                <Info size={14} className="text-stone-400" />
                Wie funktioniert das?
              </h3>
              <ol className="text-sm text-stone-600 space-y-2">
                {[
                  "Gib ein Thema und deinen Ziel-Kanal ein",
                  "Das System erstellt einen optimierten Video-Prompt",
                  "Das System generiert automatisch ein kurzes Social Media Video (15–30 Sek.)",
                  "Du erhältst den Video-Link zum Herunterladen und Posten",
                ].map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="w-5 h-5 rounded-full bg-fox-fire text-white flex items-center justify-center text-xs flex-shrink-0 font-bold">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Batch — Gürtel+Hosenträger: Tab ist bei ausgeschaltetem Flag weg ── */}
      {VIDEO_BATCH_ENABLED && activeTab === "batch" && <BatchPanel />}

      {/* ── Tab: Logs ──────────────────────────────────────────────────── */}
      {activeTab === "logs" && <LogsPanel refreshToken={logsRefreshToken} />}
    </div>
  );
};

export default VideoPage;
